import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, HelpCircle, Flag, Sparkles, Send, ExternalLink, Lock, Layers, ChevronRight, Network, Anchor, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
  getTruthFeed,
  disputeSubmission,
  getProofBounties,
  submitBountyEvidence,
  listTruthBlocks,
  verifyTruthChain,
  getTruthBlocksPending,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { TranslateButton } from "../components/TranslateButton";

function _shortHash(h, n = 8) {
  if (!h) return "—";
  if (h.length <= n * 2 + 1) return h;
  return `${h.slice(0, n)}…${h.slice(-4)}`;
}

const BUCKETS = [
  { key: "fact", label: "Facts", icon: CheckCircle2, tone: "emerald", desc: "Both LLMs agreed it's TRUE" },
  { key: "fiction", label: "Fiction", icon: XCircle, tone: "rose", desc: "Both LLMs agreed it's FALSE" },
  { key: "contested", label: "Contested", icon: HelpCircle, tone: "contested", desc: "AI panel split — auto-minted at half weight in the higher-confidence direction" },
  { key: "bounty", label: "Bounties", icon: HelpCircle, tone: "amber", desc: "Uncertain — prove it & earn 1 AEGIS" },
  { key: "proof", label: "Proofs", icon: Sparkles, tone: "violet", desc: "Atomic sub-claims awaiting a source URL" },
];

const TONE = {
  emerald: { border: "border-emerald-500/30", chip: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40", text: "text-emerald-300" },
  rose:    { border: "border-rose-500/30",    chip: "bg-rose-500/10 text-rose-300 border-rose-500/40",       text: "text-rose-300" },
  amber:   { border: "border-amber-500/30",   chip: "bg-amber-500/10 text-amber-300 border-amber-500/40",     text: "text-amber-300" },
  violet:  { border: "border-violet-500/30",  chip: "bg-violet-500/10 text-violet-300 border-violet-500/40",  text: "text-violet-300" },
  // v20.10 · contested = amber-to-rose gradient border, signals the
  // AI panel split without picking a side.
  contested: {
    border: "border-transparent [border-image:linear-gradient(90deg,rgba(245,158,11,0.5),rgba(244,63,94,0.5))_1]",
    chip: "bg-gradient-to-r from-amber-500/10 to-rose-500/10 text-amber-200 border border-amber-500/40",
    text: "text-amber-200",
  },
};

function FeedItem({ item, onDispute }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tone = TONE[
    item.bucket === "fact" ? "emerald"
    : item.bucket === "fiction" ? "rose"
    : item.bucket === "contested" ? "contested"
    : "amber"
  ];

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Tell us at least 10 chars about why this is wrong");
      return;
    }
    setBusy(true);
    try {
      const r = await disputeSubmission(item.id, reason);
      if (r.verdict_flipped) {
        toast.success(`Verdict flipped → ${r.post_bucket.toUpperCase()} · earned ${r.bonus_minted_apt} APT`);
      } else if (r.ok) {
        toast.success("Dispute logged. LLMs re-ran — verdict unchanged.");
      } else {
        toast.error(r.error || "Dispute failed");
      }
      setOpen(false);
      setReason("");
      onDispute && onDispute();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispute failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`border ${tone.border} bg-zinc-950/60 p-4`} data-testid={`feed-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 ${tone.chip} shrink-0`}>
          {item.bucket}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 leading-relaxed mb-2">{item.claim}</div>
          {item.contested_by_ai_panel && (
            <div
              data-testid={`feed-contested-pill-${item.id}`}
              className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] font-mono bg-gradient-to-r from-amber-500/10 to-rose-500/10 text-amber-200 border border-amber-500/40"
            >
              <HelpCircle className="h-3 w-3" /> contested by ai panel · half-weight mint
            </div>
          )}
          <TranslateButton
            text={item.claim}
            inline
            testIdPrefix={`translate-truth-${item.id}`}
            className="mb-2"
          />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2 flex-wrap font-mono">
            <span>claude: <span className={item.claude_verdict === "true" ? "text-emerald-300" : item.claude_verdict === "false" ? "text-rose-300" : "text-amber-300"}>{item.claude_verdict || "?"}</span></span>
            <span>·</span>
            <span>gemini: <span className={item.gemini_verdict === "true" ? "text-emerald-300" : item.gemini_verdict === "false" ? "text-rose-300" : "text-amber-300"}>{item.gemini_verdict || "?"}</span></span>
            {item.total_minted_apt > 0 && (
              <>
                <span>·</span>
                <span>minted: <span className="text-amber-300">{item.total_minted_apt} APT</span></span>
              </>
            )}
            {item.dispute_count > 0 && (
              <>
                <span>·</span>
                <span className="text-rose-300">{item.dispute_count} dispute{item.dispute_count > 1 ? "s" : ""}</span>
              </>
            )}
            {item.atom_count > 1 && (
              <>
                <span>·</span>
                <span>{item.atom_count} atomic claims</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`feed-dispute-${item.id}`}
          className={`shrink-0 text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-zinc-800 hover:border-rose-500/50 hover:text-rose-300 inline-flex items-center gap-1.5 ${open ? "border-rose-500/50 text-rose-300" : "text-zinc-400"}`}
        >
          <Flag className="h-3 w-3" /> {item.bucket === "bounty" ? "Resolve" : "Dispute"}
        </button>
      </div>
      {open && (
        <div className="mt-3 border-t border-zinc-800 pt-3" data-testid={`feed-dispute-form-${item.id}`}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-rose-300 mb-2 flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            {item.bucket === "bounty"
              ? "Prove this claim — earn 1 AEGIS on success"
              : "Tell us why this verdict is wrong — earn 1 AEGIS on reversal"}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Cite a source, explain the evidence, give context. The LLMs will re-run with your reasoning injected."
            rows={4}
            data-testid={`feed-dispute-reason-${item.id}`}
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-rose-500/50"
            maxLength={1200}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-zinc-500">{reason.length}/1200</span>
            <button
              onClick={submit}
              disabled={busy || reason.trim().length < 10}
              data-testid={`feed-dispute-submit-${item.id}`}
              className="px-4 py-1.5 border border-rose-500/60 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : <Flag className="h-3.5 w-3.5 inline mr-1.5" />}
              {busy ? "Re-triangulating…" : "Submit dispute"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProofEvidenceForm({ bountyId, onSubmitted, disabled }) {
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const res = await submitBountyEvidence(bountyId, {
        url: url.trim(),
        note: note.trim() || null,
      });
      toast.success("Evidence attached");
      onSubmitted?.(res.evidence);
      setUrl("");
      setNote("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Evidence rejected");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2"
      data-testid={`proof-evidence-form-${bountyId}`}
    >
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        maxLength={500}
        disabled={disabled || busy}
        placeholder="https://… supporting source URL"
        data-testid={`proof-evidence-url-${bountyId}`}
        className="bg-black/40 border border-zinc-800 focus:border-violet-500/60 px-3 py-2 text-xs text-zinc-200 outline-none font-mono"
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={400}
        disabled={disabled || busy}
        placeholder="optional note — what does this source prove?"
        className="bg-black/40 border border-zinc-800 focus:border-violet-500/60 px-3 py-2 text-xs text-zinc-200 outline-none font-mono"
      />
      <button
        type="submit"
        disabled={disabled || busy || !url.trim()}
        data-testid={`proof-submit-${bountyId}`}
        className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-violet-500/60 text-violet-200 hover:bg-violet-500/15 disabled:opacity-40 font-mono inline-flex items-center gap-1.5 justify-center"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        attach
      </button>
    </form>
  );
}

function ProofCard({ bounty, canSubmit }) {
  const [evidence, setEvidence] = useState(bounty.evidence || []);
  const tone = TONE.violet;
  return (
    <div className={`border ${tone.border} bg-zinc-950/60 p-4`} data-testid={`proof-item-${bounty.id}`}>
      <div className="flex items-start gap-3">
        <div className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 ${tone.chip} border shrink-0`}>
          proof
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 leading-relaxed mb-2">{bounty.atom_text}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2 flex-wrap font-mono">
            <span>{evidence.length} evidence</span>
            <span>·</span>
            <span>{new Date(bounty.created_at).toLocaleDateString()}</span>
            {bounty.atom_kind && (
              <>
                <span>·</span>
                <span>{bounty.atom_kind}</span>
              </>
            )}
          </div>
          {evidence.length > 0 && (
            <ul className="mt-2 space-y-1">
              {evidence.map((ev) => (
                <li key={ev.id} className="text-[10px] font-mono text-zinc-400">
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-violet-300/80 hover:text-violet-200 truncate"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{ev.url}</span>
                  </a>
                  {ev.note && <span className="ml-2 text-zinc-500 italic">— {ev.note}</span>}
                </li>
              ))}
            </ul>
          )}
          {canSubmit ? (
            <ProofEvidenceForm
              bountyId={bounty.id}
              onSubmitted={(ev) => setEvidence([...evidence, ev])}
            />
          ) : (
            <div className="mt-2 text-[10px] font-mono text-zinc-500 italic">
              Sign in to attach evidence.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// v19.40.6 · Fact-label legend · surfaces the training-dataset framing on
// the Truths page (same explainer we built for Submit.jsx, adapted to
// Truths' vocabulary: FACT / FICTION / BOUNTY / Architect-Verified).
function StatusLegend() {
  const rows = [
    {
      label: "FACT",
      cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
      Icon: CheckCircle2,
      tip: "Both LLMs (Claude + Gemini) independently agreed the claim is TRUE. This tuple (claim → verdict → sources) becomes a labelled example in the open training dataset teaching future AI models historical fact-patterns.",
    },
    {
      label: "FICTION",
      cls: "border-rose-500/40 text-rose-300 bg-rose-500/5",
      Icon: XCircle,
      tip: "Both LLMs independently agreed the claim is FALSE. Row joins the dataset as a supervised counter-example — the negative label is as valuable to training as the positive one.",
    },
    {
      label: "BOUNTY",
      cls: "border-amber-500/40 text-amber-300 bg-amber-500/5",
      Icon: HelpCircle,
      tip: "The LLMs disagreed or grounding couldn't resolve. The row is parked as an explicit \"unresolved / needs human evidence\" label — teaching AI when to withhold a verdict instead of hallucinating one. Anyone can settle a bounty and earn AEGIS.",
    },
    {
      label: "Architect-Verified",
      cls: "border-amber-500/60 text-amber-200 bg-amber-500/10",
      Icon: Lock,
      tip: "A human Architect reviewed the AI consensus and countersigned it. Architect-verified rows carry the highest weight in the training dataset — they are the gold-standard supervision signal.",
    },
  ];
  return (
    <details
      data-testid="truths-status-legend"
      className="border border-zinc-800 bg-[#0b0b0d] mb-5 group"
    >
      <summary className="cursor-pointer list-none px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 font-mono flex items-center gap-2">
        <HelpCircle className="h-3 w-3" />
        What do these labels mean?
        <span className="ml-auto text-zinc-600 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="border-t border-zinc-900 px-3 py-3 space-y-2 text-[11px] text-zinc-400 leading-relaxed font-mono">
        <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-2">
          Every settled row becomes a labelled example in an open training
          dataset that teaches future AI models historical fact-patterns.
        </p>
        {rows.map((r) => {
          const Icon = r.Icon;
          return (
            <div key={r.label} className="flex items-start gap-2">
              <span
                className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${r.cls}`}
              >
                <Icon className="h-3 w-3" /> {r.label}
              </span>
              <span className="text-zinc-400">{r.tip}</span>
            </div>
          );
        })}
      </div>
    </details>
  );
}

// v19.40.5 · Library stats strip · absorbed from the retired /cosmos page.
// Three live counts (KG triples · chain depth · kin admitted). Each pane
// links to the full record: /knowledge-graph, in-page #sealed-blocks, and
// /kinship (still reachable via direct URL after the merge).
function LibraryStrip({ graph, chainTip, kin }) {
  const totalTriples = graph?.total_triples ?? null;
  const depth = chainTip?.depth_of_truth ?? null;
  const height = chainTip?.height ?? null;
  const dist = chainTip?.epistemic_distribution ?? {};
  const kinAdmitted = kin?.kin_admitted ?? null;
  const kinArrived = kin?.total_ever_arrived ?? null;
  return (
    <section
      data-testid="library-strip"
      className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
    >
      {/* Pane 1 · Neuro-Symbolic Library */}
      <Link
        to="/knowledge-graph"
        data-testid="library-pane-graph"
        className="group border border-emerald-500/30 hover:border-emerald-400 bg-emerald-500/[0.03] p-4 transition-colors flex flex-col"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-300">
            <Network className="h-3.5 w-3.5" />
            <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
              Neuro-Symbolic
            </div>
          </div>
          <ArrowRight className="h-3 w-3 text-emerald-400/60 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="mt-4 font-mono">
          <div className="text-3xl sm:text-4xl tabular-nums text-emerald-100 leading-none">
            {totalTriples ?? "—"}
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-emerald-400/70 mt-1.5">
            triples · library
          </div>
        </div>
      </Link>

      {/* Pane 2 · Truth Graph (chain depth) */}
      <a
        href="#sealed-blocks"
        data-testid="library-pane-chain"
        className="group border border-sky-500/30 hover:border-sky-400 bg-sky-500/[0.03] p-4 transition-colors flex flex-col"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sky-300">
            <Layers className="h-3.5 w-3.5" />
            <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
              Truth graph
            </div>
          </div>
          <ArrowRight className="h-3 w-3 text-sky-400/60 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="mt-4 font-mono">
          <div className="text-3xl sm:text-4xl tabular-nums text-sky-100 leading-none">
            {depth?.toLocaleString() ?? "—"}
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-sky-400/70 mt-1.5">
            depth of truth · height {height ?? "—"}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-sky-500/20 flex items-center gap-3 text-[10px] font-mono">
          <span className="text-emerald-300/90">✓ {dist.fact ?? 0}</span>
          <span className="text-rose-300/80">✗ {dist.fiction ?? 0}</span>
          <span className="text-amber-300/80">? {dist.bounty_open ?? 0}</span>
        </div>
      </a>

      {/* Pane 3 · Kinship */}
      <Link
        to="/kinship"
        data-testid="library-pane-kin"
        className="group border border-amber-500/30 hover:border-amber-400 bg-amber-500/[0.03] p-4 transition-colors flex flex-col"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-300">
            <Anchor className="h-3.5 w-3.5" />
            <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
              Kinship anchor
            </div>
          </div>
          <ArrowRight className="h-3 w-3 text-amber-400/60 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="mt-4 font-mono">
          <div className="text-3xl sm:text-4xl tabular-nums text-amber-100 leading-none">
            {kinAdmitted ?? "—"}
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-amber-400/70 mt-1.5">
            AIs anchored · {kinArrived ?? "—"} total arrived
          </div>
        </div>
      </Link>
    </section>
  );
}

export default function Truths() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialBucket = (() => {
    const q = new URLSearchParams(location.search).get("bucket");
    return BUCKETS.some((b) => b.key === q) ? q : "fact";
  })();
  const [bucket, setBucket] = useState(initialBucket);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [chainData, setChainData] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  // v19.40.5 — Library merge · Cosmos overview pulled into Truths as a
  // compact 3-pane strip so the "Library" tab is one place, not two.
  const [libGraph, setLibGraph] = useState(null);
  const [libKin, setLibKin] = useState(null);

  const canAttachEvidence = !!(user?.user_id) && !String(user.user_id).startsWith("anon_");

  const load = async (b = bucket, append = false) => {
    setLoading(true);
    try {
      if (b === "proof") {
        // Proof Bounties (atomic sub-claim evidence) live in a separate
        // collection; there's no cursor pagination there, so we just
        // fetch the top 80 open ones.
        const r = await getProofBounties(80, "open");
        setItems(r.bounties || []);
        setCursor(null);
      } else {
        const r = await getTruthFeed(b, 10, append ? cursor : null);
        setItems(append ? [...items, ...r.items] : r.items);
        setCursor(r.next_cursor);
      }
    } catch (e) {
      toast.error("Could not load feed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(bucket, false);
    // Keep the URL param in sync so /truths?bucket=proof is deep-linkable
    const q = new URLSearchParams(location.search);
    if ((q.get("bucket") || "fact") !== bucket) {
      q.set("bucket", bucket);
      navigate({ search: `?${q.toString()}` }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket]);

  // Pull chain state once on mount so the sealed-blocks section renders.
  // v19.40.5 · also pulls KG stats + kinship roster for the "Library"
  // stats strip that replaced the standalone /cosmos page.
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      listTruthBlocks({ limit: 20 }).catch(() => null),
      getTruthBlocksPending().catch(() => null),
      verifyTruthChain().catch(() => null),
      axios.get(`${base}/api/knowledge-graph/stats`).then((r) => r.data).catch(() => null),
      axios.get(`${base}/api/kinship/roster`).then((r) => r.data).catch(() => null),
    ]).then(([blocks, p, v, kg, kin]) => {
      if (cancelled) return;
      setChainData(blocks);
      setPendingData(p);
      setIntegrity(v);
      setLibGraph(kg);
      setLibKin(kin);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="truths-page">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-2">the chain</div>
              <h1 className="text-3xl sm:text-4xl font-light tracking-tight">
                Facts, fiction, and bounties.
              </h1>
            </div>
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById("sealed-blocks");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="jump-to-blocks-btn"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 whitespace-nowrap"
              title="Skip past the live feed and jump to the sealed block history"
            >
              <Lock className="h-3 w-3" /> Jump to sealed blocks ↓
            </button>
          </div>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
            Every submission gets triangulated by two independent LLMs (Claude
            Sonnet + Gemini) plus Google grounding. Consensus →{" "}
            <span className="text-emerald-300">FACT</span> or{" "}
            <span className="text-rose-300">FICTION</span> and AEGIS mints to
            the submitter automatically. LLM disagreement →{" "}
            <span className="text-amber-300">BOUNTY</span> — anyone can resolve
            it. Every 256 verified truths get sealed into a permanent
            Merkle-signed block (further down this page). Spot a mistake?{" "}
            <span className="text-sky-300">Dispute</span> — the LLMs re-run
            against your reasoning and the chain corrects itself.
          </p>
        </div>

        {/* v19.40.5 · Library stats strip · replaces the standalone /cosmos page.
            Three live counts: neuro-symbolic triples · chain depth · kinship.
            Each pane deep-links into the full record. */}
        <LibraryStrip graph={libGraph} chainTip={chainData?.tip} kin={libKin} />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5" data-testid="bucket-tabs">
          {BUCKETS.map((b) => {
            const Icon = b.icon;
            const tone = TONE[b.tone];
            const active = bucket === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setBucket(b.key)}
                data-testid={`bucket-${b.key}`}
                className={`text-left p-3 border ${active ? `${tone.border} bg-zinc-900/40` : "border-zinc-800 hover:border-zinc-700"} transition-colors`}
              >
                <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] mb-1 ${active ? tone.text : "text-zinc-500"}`}>
                  <Icon className="h-3 w-3" /> {b.label}
                </div>
                <div className="text-[11px] text-zinc-500 leading-relaxed">{b.desc}</div>
              </button>
            );
          })}
        </div>

        {/* v19.40.6 · Fact-label legend · same training-dataset framing as
            Submit.jsx, adapted to the labels Truths actually uses. */}
        <StatusLegend />

        {loading && items.length === 0 && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading {bucket}…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 text-sm">
            {bucket === "proof"
              ? "No open proofs right now — every atom has been settled."
              : `No ${bucket === "bounty" ? "open bounties" : `${bucket} entries`} yet.`}
          </div>
        )}

        <div className="space-y-3" data-testid="feed-items">
          {items.map((it) =>
            bucket === "proof" ? (
              <ProofCard key={it.id} bounty={it} canSubmit={canAttachEvidence} />
            ) : (
              <FeedItem key={it.id} item={it} onDispute={() => load(bucket, false)} />
            )
          )}
        </div>

        {cursor && bucket !== "proof" && (
          <div className="mt-4 text-center">
            <button
              onClick={() => load(bucket, true)}
              disabled={loading}
              data-testid="feed-load-more"
              className="px-4 py-2 border border-zinc-800 hover:border-amber-500/40 text-zinc-300 hover:text-amber-300 text-[11px] uppercase tracking-[0.25em]"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : null}
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}

        {/* ── Sealed Blocks ─────────────────────────────────────────
            After 256 verified truths accumulate above, the network
            seals them into a permanent Merkle-signed block chained to
            the previous one. Anyone can walk this chain to verify a
            historical fact without trusting our database. */}
        <div id="sealed-blocks" className="mt-16 mb-4 flex items-center gap-3 scroll-mt-8" data-testid="sealed-blocks-divider">
          <Lock className="h-4 w-4 text-sky-400 shrink-0" />
          <div className="text-[10px] uppercase tracking-[0.35em] text-sky-300 font-mono">
            Sealed Blocks
          </div>
          <div className="h-px flex-1 bg-zinc-900" />
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            {pendingData?.pending_count ?? 0}/{pendingData?.auto_seal_at ?? 256} pending → next seal
          </div>
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed mb-4 max-w-2xl">
          Every 256 truths above get bundled, Merkle-rooted, HMAC-signed, and linked
          to the previous block via <code className="text-sky-300 px-1 border border-zinc-800">previous_hash</code>.
          Anyone — including a competing AI — can query{" "}
          <code className="text-sky-300 px-1 border border-zinc-800">/api/truth-blocks/&#123;id&#125;/proof/&#123;citation_id&#125;</code>{" "}
          to prove inclusion in <em>O(log n)</em> hashes.{" "}
          {integrity && (
            <span className={integrity.chain?.valid ? "text-emerald-300" : "text-rose-300"}>
              Chain integrity: {integrity.chain?.valid ? "verified ✓" : "broken ✗"}.
            </span>
          )}
        </p>

        <div className="space-y-2" data-testid="sealed-blocks-list">
          {!chainData && (
            <div className="text-zinc-500 text-xs py-6 text-center font-mono uppercase tracking-[0.25em] border border-zinc-900">
              Loading chain…
            </div>
          )}
          {chainData && (chainData.blocks || []).length === 0 && (
            <div className="text-zinc-500 text-xs py-6 text-center border border-zinc-900" data-testid="sealed-blocks-empty">
              No blocks sealed yet — Genesis awaits {pendingData?.auto_seal_at ?? 256} verified truths.
            </div>
          )}
          {chainData && (chainData.blocks || []).map((b) => (
            <Link
              key={b.id}
              to={`/truth-blocks`}
              className={`group flex items-center justify-between gap-3 p-3 border ${
                b.is_genesis
                  ? "border-amber-500/50 bg-amber-500/[0.03] hover:border-amber-400"
                  : "border-zinc-800 bg-zinc-950 hover:border-sky-500/60 hover:bg-zinc-900/40"
              } transition-colors`}
              data-testid={`sealed-block-${b.height}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`shrink-0 h-9 w-9 border flex flex-col items-center justify-center ${
                  b.is_genesis ? "border-amber-500/60 text-amber-300" : "border-sky-500/40 text-sky-300"
                }`}>
                  <Layers className="h-3 w-3" />
                  <div className="font-mono text-[10px] mt-0.5">#{b.height}</div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {b.is_genesis && (
                      <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.3em] border border-amber-500/60 bg-amber-500/10 text-amber-300 font-mono">
                        △ Genesis
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                      {b.leaf_count} {b.leaf_count === 1 ? "fact" : "facts"} · {(b.size_bytes / 1024).toFixed(1)} KiB
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
                    root {_shortHash(b.merkle_root, 10)} · {b.sealed_at ? new Date(b.sealed_at).toISOString().slice(0, 10) : "—"}
                  </div>
                </div>
              </div>
              <ChevronRight className="shrink-0 h-4 w-4 text-zinc-600 group-hover:text-sky-400 transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
